import { router } from '@inertiajs/vue3'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { stripHTML } from '@/Composables/common.js'

export const useBooking = defineStore('booking', {
	namespaced: true,

	state: () => ({
		officetimes: [],

		pickup: {
			locations: [],
		},

		dropoff: {
			locations: [],
		},

		age: [],

		steps: [
			{
				id: 1,
				title: 'Select',
				isCurrent: false,
				isCompleted: false, // switch back to false
				isDisabled: false,
				path: '/select',
			},
			{
				id: 2,
				title: 'Extras',
				isCurrent: false,
				isCompleted: false,
				isDisabled: false,

				path: '/extras',
			},
			{
				id: 3,
				title: 'Payment',
				isCurrent: false,
				isCompleted: false,
				isDisabled: false,

				path: '/payment',
			},
			{
				id: 4,
				title: 'Confirmation',
				isCurrent: false,
				isCompleted: false,
				isDisabled: false,

				path: '/confirmation',
			},
		],

		selected: {
			pickup: {
				location: '',
				location_id: null,
				date: '',
				time: '',
			},

			dropoff: {
				location: '',
				location_id: null,
				date: '',
				time: '',
			},

			age: {
				value: '',
				id: null,
			},

			campaigncode: '',

			vehicle: {
				categoryid: null,
				categorytypeid: null,
				type: '',
				model: '',
				totalrate: null,
				dailyrate: null,
			},

			extra: {
				optional: [],
				optionaltotal: [],
				insurance: null,
			},

			customer: {
				firstname: '',
				lastname: '',
				email: '',
				mobile: null,
				countryname: '',
			},

			payment: {
				method: '',
				transactionid: '',
			},
		},

		availablecars: [],
		locationfees: [],
		optionalfees: [],
		insuranceoptions: [],
		calculatetotal: [],

		searchButton: {
			text: 'Book now',
			isFirstSearchComplete: false,
		},

		isLoading: false,
		isSubmitting: false,
		isFromHome: false,
		errors: null,
		errorPayment: null,
	}),

	getters: {
		getSteps: (state) => state.steps,
		getIsFirstSearchComplete: (state) =>
			state.searchButton.isFirstSearchComplete,

		getAvailableCars: (state) => state.availablecars,
		getLocationFees: (state) => state.locationfees,
		getOptionalFees: (state) => state.optionalfees,
		getInsuranceOptions: (state) => state.insuranceoptions,

		getDefaultAge: (state) => {
			const defaultAges = state.age.filter((age) => age?.isdefault)
			return defaultAges.slice(-1)[0] ?? null
		},

		getSelectedVehicle: (state) => state.selected.vehicle,
		getSelectedPickup: (state) => state.selected.pickup,
		getSelectedDropoff: (state) => state.selected.dropoff,
		getSelectedAge: (state) => state.selected.age,
		getSelectedExtraOptional: (state) => state.selected.extra.optional,
		getSelectedExtraOptionalTotal: (state) =>
			state.selected.extra.optionaltotal.reduce(
				(sum, num) => sum + num,
				0
			) || 0,
		getSelectedExtraInsurance: (state) => state.selected.extra.insurance,
		getSelectedPayment: (state) => state.selected.payment,

		isBabySeatSelected: (state) =>
			state.selected.extra.optional.some((item) => item.id === 50),
		isBoosterSeatSelected: (state) =>
			state.selected.extra.optional.some((item) => item.id === 51),
		isInsuranceStandardSelected: (state) =>
			state.selected.extra.insurance === 59,
		isInsuranceLooseExcessSelected: (state) =>
			state.selected.extra.insurance === 28,

		getCustomerDetails: (state) => state.selected.customer,

		getCalculateTotal: (state) => state.calculatetotal,

		isPickupSelected: (state) => {
			const { location, time, date } = state.selected.pickup
			return Boolean(location && time && date)
		},

		isDropoffSelected: (state) => {
			const { location, time, date } = state.selected.dropoff
			return Boolean(location && time && date)
		},

		getSearchButtonText: (state) => state.searchButton.text,
		getIsLoading: (state) => state.isLoading,
		getIsSubmitting: (state) => state.isSubmitting,
		getIsFromHome: (state) => state.isFromHome,

		getAllCompleted: (state) => state.steps[3].isCompleted,

		hasErrors: (state) => state.errors,
		hasErrorPayment: (state) => state.errorPayment,
		getErrors: (state) => {
			if (typeof state.errors === 'string') {
				return state.errors
			} else {
				const messages = Object.entries(state.errors ?? {})
				const errorArr = messages.map(([, message]) => message)
				return errorArr[0]
			}
		},
	},

	actions: {
		generateTimeSlots(timeStart, timeEnd) {
			const slots = []
			let start = new Date(`1970-01-01T${timeStart}:00`)
			const end = new Date(`1970-01-01T${timeEnd}:00`)

			while (start <= end) {
				const formattedTime = start.toTimeString().slice(0, 5) // Extract "HH:MM"
				slots.push({
					value: formattedTime,
					label: formattedTime,
				})

				// Add 30 minutes
				start.setMinutes(start.getMinutes() + 30)
			}

			return slots
		},

		addPlusToLastAgeOption() {
			const optionIndex = this.age.findIndex((age) => age?.label === 25)
			this.age[optionIndex].label += '+'
		},

		generateLocationOptions(data) {
			if (!Array.isArray(data)) return []

			const firstOfficeTime = this.officetimes[0] || {}

			return data.map((item) => {
				const timeStart =
					item.officeopeningtime ||
					firstOfficeTime.startpickup ||
					'N/A'
				const timeEnd =
					item.officeclosingtime || firstOfficeTime.endpickup || 'N/A'

				return {
					label: item.location,
					value: item.id,
					id: item.id,
					time_slots: this.generateTimeSlots(timeStart, timeEnd),
					holidays: item.holidays,
				}
			})
		},

		generateDriverAges(data) {
			if (!Array.isArray(data)) return []

			return data.map((item) => ({
				label: item.driverage,
				value: item.id,
				id: item.id,
				isdefault: item.isdefault,
			}))
		},

		async setOptions(data = {}) {
			// console.log(data.officetimes)
			// RCM Step 1 -
			// Setup retrieved pickup locations, time slots and holidays from BookingController.
			const hasData = data && Object.keys(data).length > 0

			if (!hasData) {
				this.errors = 'No data received. Please try again later.'
				return
			}

			// Setup dropoff locations, time slots and holidays.
			this.officetimes = data.officetimes || []
			this.pickup.locations = this.generateLocationOptions(data.pickup)
			this.dropoff.locations = this.generateLocationOptions(data.dropoff)
			this.age = this.generateDriverAges(data.driver_ages)

			this.addPlusToLastAgeOption()
		},

		setAvailableResults(data) {
			// RCM Step 2 -
			// Setup and retrieved available cars and locations fees from BookingController Step 2.
			if (data) {
				this.availablecars = data.availablecars
				this.locationfees = data.locationfees
				this.errors = data.locationfees[0]?.availablemessage
			} else {
				this.availablecars = []
				this.locationfees = []
				this.errors = []
			}
		},

		resetIsCompleted(index) {
			this.steps[index].isCompleted = false
		},

		highlightStepCurrent(index) {
			this.steps = this.steps.map((step, i) => {
				if (i < index) {
					step.isCurrent = false
					step.isCompleted = true
					step.isDisabled = index >= 3
				} else if (i === index) {
					step.isCurrent = true
					step.isCompleted = index >= 3 && i === 3 // Complete step 3 when we reach it or go past it
					step.isDisabled = true
				} else {
					step.isCurrent = false
					step.isCompleted = false
					step.isDisabled = false
				}
				return step
			})
		},

		goToStep(index) {
			this.highlightStepCurrent(index)

			switch (index) {
				case 1:
					router.visit(this.steps[1].path)
					break
				case 0:
					router.visit(this.steps[0].path)
					break
				default:
					router.visit('/')
					break
			}
		},

		checkRouteAndUpdateSteps(urlPath) {
			const uri = new URL(urlPath, window.location.origin).pathname
				.slice(1) // Remove leading slash
				.split('/')[0] // Take only first segment
				.replace(/\?.*$/, '') // Remove query params if they somehow got included

			const capitalizedUri = uri
				? uri.charAt(0).toUpperCase() + uri.slice(1)
				: ''

			const stepToUpdate = this.steps.find(
				(step) => step.title === capitalizedUri
			)

			if (stepToUpdate) {
				stepToUpdate.path = urlPath
			}

			const stepId =
				this.steps.find((step) => step.title === capitalizedUri)?.id - 1
			this.highlightStepCurrent(stepId)
		},

		getLocationBySelectedId(id, type = 'pickup') {
			return (
				this[type]?.locations.find((loc) => loc.id === id)?.label ??
				'Unknown'
			)
		},

		getDriverAgeById(id) {
			return this.age.find((age) => age.id === id)?.label ?? 0
		},

		getSelectedDriverAgeBySelectedId(id) {
			return this.selected.age.find((age) => age.id === id)?.label ?? 0
		},

		mapValues(values) {
			this.selected.pickup.date = values.pickupdate
			this.selected.dropoff.date = values.dropoffdate
			this.selected.pickup.location_id = values.pickuplocationid
			this.selected.pickup.time = values.pickuptime
			this.selected.dropoff.location_id = values.dropofflocationid
			this.selected.dropoff.time = values.dropofftime

			const driverAge = this.getDriverAgeById(values.ageid)
			this.selected.age.value = driverAge.toString()
			this.selected.age.id = values.ageid
			this.selected.campaigncode = values.campaigncode

			const pickupLocationName = this.getLocationBySelectedId(
				values.pickuplocationid,
				'pickup'
			)

			const dropoffLocationName = this.getLocationBySelectedId(
				values.dropofflocationid,
				'dropoff'
			)

			this.selected.pickup.location = pickupLocationName
			this.selected.dropoff.location = dropoffLocationName
		},

		async mapVehicle(payload) {
			this.selected.vehicle.categoryid = payload.vehiclecategoryid
			this.selected.vehicle.categorytypeid = payload.vehiclecategorytypeid
			this.selected.vehicle.type = payload.vehiclecategory
			this.selected.vehicle.model = stripHTML(payload.vehicledescription1)
			this.selected.vehicle.totalrate = payload.totalrateafterdiscount
			this.selected.vehicle.dailyrate = payload.discounteddailyrate
		},

		async selectVehicle(payload) {
			await this.mapVehicle(payload)

			this.steps[0].isCompleted = true
			this.steps[1].isCurrent = true
		},

		updateButtonState() {
			this.searchButton.isFirstSearchComplete = true
			this.searchButton.text = 'Update'
		},

		reset() {
			this.$reset()
		},
	},

	persist: {
		storage: sessionStorage,
		pick: ['searchButton', 'selected', 'steps', 'isFromHome'],
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useBooking, import.meta.hot))
}
